/**
 * Utility functions for detecting organization identifiers from URL.
 * Used for enterprise SSO subdomain-based organization detection.
 */

/**
 * Extracts the subdomain from the current URL hostname.
 * Returns null if no valid organization subdomain is found.
 *
 * @returns The organization subdomain, or null if not present
 *
 * @example
 * // For hostname "xyz-org.workspace.gannet.ai"
 * getSubdomain() // returns "xyz-org"
 *
 * @example
 * // For hostname "workspace.gannet.ai"
 * getSubdomain() // returns null
 */
export const getSubdomain = (): string | null => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  if (
    hostname === 'dev.workspace.gannet.ai' ||
    hostname === 'workspace.gannet.ai'
  ) {
    return null;
  }

  if (parts.length >= 3) {
    const subdomain = parts[0];
    if (subdomain !== 'app' && subdomain !== 'www') {
      return subdomain;
    }
  }
  return null;
};

/**
 * Gets the organization identifier from the URL query parameter.
 * Useful for localhost testing where subdomains are not available.
 *
 * @returns The organization identifier from the 'org' query param, or null
 *
 * @example
 * // For URL "http://localhost:3000?org=xyz-org"
 * getOrgFromQuery() // returns "xyz-org"
 */
export const getOrgFromQuery = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get('org');
};

/**
 * Gets the organization identifier, trying subdomain first, then query param.
 * This is the primary function to use for detecting the organization context.
 *
 * @returns The organization identifier from subdomain or query param, or null
 *
 * @example
 * // For hostname "xyz-org.workspace.gannet.ai"
 * getOrgIdentifier() // returns "xyz-org"
 *
 * @example
 * // For URL "http://localhost:3000?org=xyz-org"
 * getOrgIdentifier() // returns "xyz-org"
 */
export const getOrgIdentifier = (): string | null => {
  return getSubdomain() || getOrgFromQuery();
};
